import React, { useEffect, useState } from "react";
import "./Lessons.css";
import {
  BrowserIcon,
  DeleteIcon,
  DragIcon,
  EditIcon,
  MoviePlayIcon,
  PaperClipIcon,
  PdfIcon,
} from "../../../icons/reactIcons";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  deleteLessonThunk,
  fetchLessonListThunk,
  rearrangeLessonThunk,
} from "../../../redux/slices/lessonSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import Loader from "../../../components/custom-loader/Loader";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";
import { toast } from "react-toastify";

/* ================= ICON MAPPER ================= */
const getLessonIcon = (lessonType?: string) => {
  switch (lessonType) {
    case "video":
      return <MoviePlayIcon />;
    case "website":
      return <BrowserIcon />;
    case "document":
    default:
      return <PdfIcon />;
  }
};

const Lessons: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { weeks, loading } = useAppSelector((state) => state.lesson);

  const [lessonsData, setLessonsData] = useState<any[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [selectedLessonTitle, setSelectedLessonTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    dispatch(fetchLessonListThunk());
  }, [dispatch]);

  /* ================= FORMAT API DATA ================= */
  useEffect(() => {
    if (weeks) {
      const formatted = weeks.map((week) => ({
        weekId: week.id,
        weekName: week.name,
        lessons: week.lessons.map((lesson) => {
          const created = formatDateTime(lesson.createdAt).split(" | ");
          const updated = formatDateTime(lesson.updatedAt).split(" | ");

          return {
            lessonId: lesson.id,
            title: lesson.name,
            attachments: lesson.attachmentCount,
            createdBy: lesson.creator?.name || "-",
            createdDate: created[0],
            createdTime: created[1],
            updatedBy: lesson.updater?.name || "-",
            updatedDate: updated[0],
            updatedTime: updated[1],
            type: lesson.lessonType,
          };
        }),
      }));

      setLessonsData(formatted);
    }
  }, [weeks]);

  /* ================= DELETE ================= */
  const handleDeleteLesson = async () => {
    if (!selectedLessonId) return;

    try {
      setSubmitting(true);
      const result = await dispatch(deleteLessonThunk(selectedLessonId));

      if (deleteLessonThunk.fulfilled.match(result)) {
        dispatch(fetchLessonListThunk());
        setOpenDeleteModal(false);
        setSelectedLessonId(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= DRAG HANDLER ================= */
  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const updatedWeeks = structuredClone(lessonsData);

    const sourceWeekIdx = updatedWeeks.findIndex(
      (w: any) => w.weekId === source.droppableId,
    );
    const destWeekIdx = updatedWeeks.findIndex(
      (w: any) => w.weekId === destination.droppableId,
    );

    const [movedLesson] = updatedWeeks[sourceWeekIdx].lessons.splice(
      source.index,
      1,
    );

    updatedWeeks[destWeekIdx].lessons.splice(destination.index, 0, movedLesson);

    setLessonsData(updatedWeeks);

    const payload = {
      lessonId: draggableId,
      fromWeekId: source.droppableId,
      toWeekId: destination.droppableId,
      oldSortOrder: source.index + 1,
      newSortOrder: destination.index + 1,
    };

    try {
      const resultAction = await dispatch(rearrangeLessonThunk(payload));

      if (rearrangeLessonThunk.rejected.match(resultAction)) {
        dispatch(fetchLessonListThunk());
        toast.error("Failed to move lesson. Rolling back changes.");
      }
    } catch (error) {
      dispatch(fetchLessonListThunk());
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="lessons-container">
      <div className="lessons-header">
        <h1 className="page-title">Lessons</h1>
        <button
          className="create-btn"
          onClick={() => navigate("/lessons/create")}
        >
          Create New
        </button>
      </div>

      <div className="lessons-table-header">
        <span>Week</span>
        <span>Lessons Title</span>
        <span>Created By</span>
        <span>Updated By</span>
        <span style={{ textAlign: "right" }}>Actions</span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        {lessonsData.map((weekData) => (
          <div key={weekData.weekId} className="week-section">
            <div className="week-title">{weekData.weekName}</div>

            <Droppable droppableId={weekData.weekId}>
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="lessons-droppable"
                  style={{ minHeight: "70px" }} // ✅ important fix
                >
                  {weekData.lessons.length === 0 && (
                    <div className="empty-week">No lesson available</div>
                  )}

                  {weekData.lessons.map((lesson: any, idx: number) => (
                    <Draggable
                      key={lesson.lessonId}
                      draggableId={lesson.lessonId}
                      index={idx}
                    >
                      {(providedDrag) => (
                        <div
                          className="lesson-row"
                          ref={providedDrag.innerRef}
                          {...providedDrag.draggableProps}
                        >
                          <div
                            className="drag-cell"
                            {...providedDrag.dragHandleProps}
                          >
                            <DragIcon className="drag-icon" />
                          </div>

                          <div
                            className="lesson-title-cell"
                            onClick={() =>
                              navigate(`/lessons/details/${lesson.lessonId}`)
                            }
                          >
                            <div className={`lesson-icon ${lesson.type}`}>
                              {getLessonIcon(lesson.type)}
                            </div>

                            <div className="lesson-title-text">
                              {lesson.title}
                            </div>

                            <div className="lesson-attachments">
                              +{lesson.attachments} <PaperClipIcon />
                            </div>
                          </div>

                          <div className="lesson-created-cell">
                            <div className="name">{lesson.createdBy}</div>
                            <div className="datetime">
                              {lesson.createdDate} | {lesson.createdTime}
                            </div>
                          </div>

                          <div className="lesson-updated-cell">
                            <div className="name">{lesson.updatedBy}</div>
                            <div className="datetime">
                              {lesson.updatedDate} | {lesson.updatedTime}
                            </div>
                          </div>

                          <div className="lesson-actions">
                            <DeleteIcon
                              className="action-icon delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLessonId(lesson.lessonId);
                                setSelectedLessonTitle(lesson.title);
                                setOpenDeleteModal(true);
                              }}
                              style={{ width: "20px", height: "20px" }}
                              color="black"
                            />

                            <EditIcon
                              className="action-icon edit"
                              style={{ width: "20px", height: "20px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/lessons/edit/${lesson.lessonId}`);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        ))}
      </DragDropContext>

      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={selectedLessonTitle}
          loading={submitting}
          onClose={() => {
            setOpenDeleteModal(false);
            setSelectedLessonId(null);
          }}
          onConfirm={handleDeleteLesson}
        />
      )}
    </div>
  );
};

export default Lessons;
